import { FAQ } from "./types";

export const FAQ_DATA: FAQ[] = [
  {
    id: 1,
    question: "Is the Starter plan really free, or is it a trial?",
    answer:
      "It’s completely free, not a trial. You can use it for one free project (either creating one RFP or analyzing one RFP) every single month, for as long as you like. Our goal is to let you experience the core power of our AI with no strings attached.",
    category: "Getting Started",
    tags: ["starter", "free", "trial", "plan"],
  },
  {
    id: 2,
    question: "Do I need a credit card to sign up for the Starter plan?",
    answer:
      "No. You can sign up and start using the free tool immediately without providing any payment information.",
    category: "Getting Started",
    tags: ["starter", "signup", "credit card", "free"],
  },
  {
    id: 3,
    question: "What happens after I use my free project for the month?",
    answer:
      "You will still have access to your dashboard and past results. You won’t be able to start a new project until your monthly limit resets on the first of the next month. If you need to run more projects, you can easily upgrade to our Pro plan at any time.",
    category: "Getting Started",
    tags: ["starter", "free", "limit", "upgrade"],
  },
  {
    id: 4,
    question: "How quickly can I get my analysis results?",
    answer:
      "In minutes. For most standard RFPs, our AI completes its full analysis and generates your reports in under 10 minutes after you’ve uploaded all the documents.",
    category: "Getting Started",
    tags: ["analysis", "results", "time", "speed"],
  },
  {
    id: 5,
    question:
      "How is Nozma different from just using a generic AI like ChatGPT?",
    answer:
      "Think of a generic AI as a raw engine. Nozma is the fully-engineered car. Our platform is purpose-built for procurement with security guardrails, a deep understanding of sourcing workflows, and AI models fine-tuned on procurement data for superior accuracy. We provide a complete, reliable, and secure solution, not just a raw tool.",
    category: "Functionality",
    tags: ["ai", "comparison", "chatgpt", "procurement"],
  },
  {
    id: 6,
    question:
      'How does Nozma prevent AI "hallucinations" or inaccurate results?',
    answer: `This is at the core of our design and a key differentiator from generic, public LLMs. We’ve built a multi-layered approach to ensure accuracy and reliability:\n\n1. Purpose-Built Models: Our AI models are not general-purpose. They are fine-tuned specifically on procurement data and strategic sourcing tasks, which dramatically reduces the risk of irrelevant or fabricated outputs.\n\n2. Data Grounding & Citations: Every key insight or score our AI generates is grounded in the source material. Our reports provide direct citations and links back to the specific sentence or section in the Supplier’s document that supports the finding. This makes every result transparent and verifiable by a human.\n\n3. Structured Analysis Framework: We don’t just ask the AI an open-ended question. We run Supplier responses through a structured evaluation framework that constrains the AI to analyze against your specific requirements. This "puts the AI on rails," preventing it from going off-topic or inventing information.`,
    category: "Functionality",
    tags: ["accuracy", "hallucinations", "llm", "reliability"],
  },
  {
    id: 7,
    question: "What file formats does Nozma support?",
    answer:
      "Nozma is designed to handle the real world of procurement. You can upload most standard document formats, including PDF, Word (.docx), Excel (.xlsx), and their attachments.",
    category: "Functionality",
    tags: ["files", "formats", "pdf", "word", "excel"],
  },
  {
    id: 8,
    question: "Is my data secure on the Starter plan?",
    answer:
      "Yes. We apply the same enterprise-grade security standards to all our users, including those on the free plan. Your data is protected with end-to-end encryption.",
    category: "Security & Privacy",
    tags: ["security", "encryption", "starter", "privacy"],
  },
  {
    id: 9,
    question: "Will my company's data be used to train your AI models?",
    answer:
      "No. Absolutely not. Your proprietary data is processed in a secure, private environment and is never used to train any public or third-party AI models. Your data’s confidentiality is our highest priority.",
    category: "Security & Privacy",
    tags: ["data", "privacy", "training", "confidentiality"],
  },
  {
    id: 10,
    question: "What is the main difference between the Pro and Business plans?",
    answer:
      "The Pro plan is designed for an individual power user, giving them more volume and a full toolkit of advanced analysis and reporting features. The Business plan is for teams, adding collaboration workflows, all three of our strategic modules (Create, Analyze, Negotiate), and enterprise-grade features like Single Sign-On (SSO).",
    category: "Billing & Pricing",
    tags: ["plans", "pro", "business", "pricing"],
  },
  {
    id: 11,
    question:
      "We already use a Source-to-Pay (S2P) platform like SAP Ariba, Coupa, or Scout RFP. How is Nozma different?",
    answer:
      "That’s a great question. We see ourselves as a strategic partner that enhances, not replaces, those excellent systems. While platforms like Ariba and Coupa are fantastic for managing the end-to-end procurement process (the workflow, approvals, and transactions), Nozma provides a specialized, deep AI capability for the most complex and manual part of that process: the strategic decision-making.",
    category: "Billing & Pricing",
    tags: ["s2p", "ariba", "coupa", "scout rfp", "difference"],
  },
  {
    id: 12,
    question: 'What counts as one "project"?',
    answer:
      "A project is either creating one new RFP or analyzing one set of supplier responses.",
    category: "Billing & Pricing",
    tags: ["project", "count", "pricing"],
  },
  {
    id: 13,
    question: "Can I change my plan later?",
    answer:
      "Yes, you can upgrade, downgrade, or switch between monthly and annual plans at any time.",
    category: "Billing & Pricing",
    tags: ["plan", "upgrade", "downgrade", "subscription"],
  },
  {
    id: 14,
    question: "What happens when my Pro or Teams trial ends?",
    answer:
      "You will be automatically moved to our free Starter plan. You won't be charged unless you actively choose to purchase a plan.",
    category: "Billing & Pricing",
    tags: ["trial", "pro", "teams", "starter"],
  },
];

export const QUICK_ACTIONS = [
  {
    id: "getting-started",
    label: "Getting Started",
    category: "Getting Started",
  },
  {
    id: "functionality",
    label: "Functionality",
    category: "Functionality",
  },
  {
    id: "security",
    label: "Security",
    category: "Security & Privacy",
  },
  {
    id: "billing",
    label: "Billing",
    category: "Billing & Pricing",
  },
];

export const PRIORITY_OPTIONS = [
  { value: "low", label: "Low - General inquiry" },
  { value: "medium", label: "Medium - Need assistance" },
  { value: "high", label: "High - Urgent issue" },
] as const;
